import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import LinkOut from '@spectrum-icons/workflow/LinkOut';
import { useOpenCardInfo } from '../../common/cardInfoDialog/useOpenCardInfo';
import { setAppRoute } from '../../../data/redux/slices/appSlice';

const TIERS = [
    { key: 'one', label: 'Tier 1', hint: 'Power cap 10.99 · the dominant shells' },
    { key: 'two', label: 'Tier 2', hint: 'Power cap 10.69 · fringe cEDH' },
    { key: 'three', label: 'Tier 3', hint: 'Power cap 10.29 · niche but present' },
    { key: 'four', label: 'Tier 4', hint: 'Power cap 9.29 · seen once or twice' },
];

export function CommandersPane({ metaBlob }) {
    const [search, setSearch] = useState('');
    const [activeTier, setActiveTier] = useState('one');
    const dispatch = useDispatch();
    const openCardInfo = useOpenCardInfo();

    const tierData = useMemo(() => {
        if (!metaBlob?.meta?.tiers) return [];
        const tok = search.trim().toLowerCase();
        return TIERS.map((t) => {
            const ids = metaBlob.meta.tiers?.[t.key]?.ids || [];
            const rows = ids
                .map((id) => metaBlob.commandersMetadata?.[id])
                .filter(Boolean)
                .filter((c) =>
                    !tok ||
                    (c.names || []).some((n) => n.toLowerCase().includes(tok)),
                );
            const tierShare = rows.reduce((sum, c) => sum + Number(c.metaShare || 0), 0);
            const tierEntries = rows.reduce((sum, c) => sum + Number(c.entries || 0), 0);
            const tierWins = rows.reduce((sum, c) => sum + Number(c.wins || 0), 0);
            return { ...t, rows, tierShare, tierEntries, tierWins };
        });
    }, [metaBlob, search]);

    // Normalise metaShare against the overall #1 so the bar width stays
    // meaningful across tiers (Tier 4 cards would otherwise all look
    // visually identical at the bottom of the list).
    const globalMax = useMemo(() => {
        let max = 0;
        for (const t of tierData) for (const r of t.rows) max = Math.max(max, Number(r.metaShare || 0));
        return max || 1;
    }, [tierData]);

    if (!metaBlob?.meta?.tiers) {
        return (
            <div className="metaPaneEmpty">
                <span>Meta snapshot is still loading — check back in a moment.</span>
            </div>
        );
    }

    return (
        <div className="metaCommandersPane">
            <div className="metaFilterRow">
                <input
                    className="metaFilterInput"
                    type="text"
                    placeholder="Filter commanders…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    spellCheck={false}
                />
                <label className="metaTierPicker">
                    <span className="metaTierPickerLabel">Tier</span>
                    <select
                        className="metaTierPickerSelect"
                        value={activeTier}
                        onChange={(e) => setActiveTier(e.target.value)}
                    >
                        {tierData.map((t) => (
                            <option key={t.key} value={t.key}>
                                {t.label} · {t.rows.length}
                            </option>
                        ))}
                    </select>
                </label>
                <span className="metaFilterHint">
                    Win % = top-16 finishes divided by all top-16 slots in the event pool.
                </span>
            </div>

            {tierData.map((t) => (
                <section
                    key={t.key}
                    className={`metaTierBand metaTierBand--${t.key}${
                        activeTier === t.key ? ' metaTierBand--active' : ''
                    }`}
                >
                    <header className="metaTierHeader">
                        <div className="metaTierHeaderMain">
                            <span className={`metaTierBadge metaTierBadge--${t.key}`}>{t.label}</span>
                            <span className="metaTierCount">{t.rows.length} commander{t.rows.length === 1 ? '' : 's'}</span>
                            <span className="metaTierHint">{t.hint}</span>
                        </div>
                        <div className="metaTierStats">
                            <span className="metaTierStat">
                                <span className="metaTierStatValue">{t.tierShare.toFixed(1)}%</span>
                                <span className="metaTierStatLabel">win share</span>
                            </span>
                            <span className="metaTierStat">
                                <span className="metaTierStatValue">{t.tierEntries.toLocaleString()}</span>
                                <span className="metaTierStatLabel">entries</span>
                            </span>
                            <span className="metaTierStat">
                                <span className="metaTierStatValue">{t.tierWins.toLocaleString()}</span>
                                <span className="metaTierStatLabel">wins</span>
                            </span>
                        </div>
                    </header>

                    {t.rows.length === 0 ? (
                        <div className="metaTierEmpty">No commanders match this filter in {t.label}.</div>
                    ) : (
                        <div className="metaCommanderGrid">
                            {t.rows.map((c) => (
                                <CommanderTile
                                    key={c.id}
                                    entry={c}
                                    globalMax={globalMax}
                                    onOpenCard={(payload) => openCardInfo(payload)}
                                    onOpenPage={() =>
                                        dispatch(
                                            setAppRoute({
                                                route: `/commanders/id/${encodeURIComponent(c.id)}`,
                                            }),
                                        )
                                    }
                                />
                            ))}
                        </div>
                    )}
                </section>
            ))}
        </div>
    );
}

function CommanderTile({ entry, globalMax, onOpenCard, onOpenPage }) {
    const share = Number(entry.metaShare || 0);
    const barPct = Math.max(2, Math.min(100, (share / globalMax) * 100));
    const entries = Number(entry.entries || 0);
    const wins = Number(entry.wins || 0);
    const conversion = entries > 0 ? (wins / entries) * 100 : 0;
    const isPartner = (entry.imageUris || []).length > 1;

    // For a single commander we open its CardInfoDialog straight away.
    // Partners pop the lead (first) commander's card since the dialog is
    // single-card by design — the page button still reaches the full
    // combined commander view.
    const primaryCardId = (entry.commanderIds || [])[0] || entry.id;
    const primaryName = (entry.names || [])[0] || entry.id;
    const primaryImage = (entry.imageUris || [])[0];

    const handleTileClick = () => {
        onOpenCard({
            id: primaryCardId,
            cardId: primaryCardId,
            name: primaryName,
            imageUri: primaryImage,
        });
    };

    const handlePageClick = (e) => {
        e.stopPropagation();
        onOpenPage();
    };

    return (
        <div
            className="metaCommanderTile"
            role="button"
            tabIndex={0}
            onClick={handleTileClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTileClick();
                }
            }}
        >
            <span className="metaCommanderRank">#{entry.position}</span>

            <div className={`metaCommanderArtFrame${isPartner ? ' is-partner' : ''}`}>
                {(entry.imageUris || []).slice(0, 2).map((uri, idx) => (
                    <img
                        key={idx}
                        src={uri}
                        alt={(entry.names || [])[idx] || ''}
                        loading="lazy"
                        className={`metaCommanderArt${isPartner ? ` metaCommanderArt--${idx === 0 ? 'left' : 'right'}` : ''}`}
                    />
                ))}
            </div>

            <div className="metaCommanderMain">
                <div className="metaCommanderTopRow">
                    <div className="metaCommanderNames">
                        {(entry.names || []).map((n) => (
                            <span key={n} className="metaCommanderName">
                                {n}
                            </span>
                        ))}
                    </div>
                    <button
                        type="button"
                        className="metaCommanderPageBtn"
                        onClick={handlePageClick}
                        aria-label={`Open commander page for ${primaryName}`}
                        title="Open commander page"
                    >
                        <LinkOut size="XS" />
                        <span className="metaCommanderPageBtnLabel">Page</span>
                    </button>
                </div>

                <div className="metaCommanderShareRow">
                    <div className="metaCommanderShareBar">
                        <div
                            className="metaCommanderShareFill"
                            style={{ width: `${barPct}%` }}
                        />
                    </div>
                    <span className="metaCommanderShareValue">{share.toFixed(2)}%</span>
                </div>

                <div className="metaCommanderStatsRow">
                    <span className="metaCommanderStat">
                        <span className="metaCommanderStatLabel">Entries</span>
                        <span className="metaCommanderStatValue">{entries.toLocaleString()}</span>
                    </span>
                    <span className="metaCommanderStat">
                        <span className="metaCommanderStatLabel">Wins</span>
                        <span className="metaCommanderStatValue">{wins.toLocaleString()}</span>
                    </span>
                    <span className="metaCommanderStat">
                        <span className="metaCommanderStatLabel">Conv.</span>
                        <span className="metaCommanderStatValue">{conversion.toFixed(1)}%</span>
                    </span>
                </div>
            </div>
        </div>
    );
}
